<!-- @format -->
<template>
	<div :class="{ 'sidebar-container': true, isCollapse: isCollapse }">
		<el-scrollbar class="sidebar-menu">
			<el-menu :default-active="activeMenu" :collapse="isCollapse" mode="vertical" popper-effect="light">
				<sidebar-item
					v-for="(item, index) in isShowSidebarRouters"
					:key="item.path + index"
					:item="item"
					:show-only-children="showOnlyChildren"
					:level="0"
				/>
			</el-menu>
		</el-scrollbar>
		<div class="menu-footer">
			<hamburger
				id="hamburger-container"
				:is-active="appStore.sidebar.opened"
				class="hamburger-container"
				@toggle-click="toggleSideBar"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { type RouteLocationNormalizedLoaded } from 'vue-router';
	import { getSubSystemAliasFromLocationPathname, useGlobalStoreWithOut } from '@power/runtime-vue3';
	import { isExternal, getNormalPath } from '@power/utils';
	import SidebarItem from './sidebar-item.vue';
	import Hamburger from './hamburger.vue';
	import { useMenuStoreWithOut } from '@/store/modules/menu';
	import { useAppStoreWithOut } from '@/store/modules/app';
	import { type Router } from '~/types';
	const menuStore = useMenuStoreWithOut();
	interface Props {
		showOnlyChildren?: boolean;
		modelValue: Router[];
		route: RouteLocationNormalizedLoaded;
	}

	const props = withDefaults(defineProps<Props>(), {});

	const emits = defineEmits(['update:modelValue']);

	const route2 = useRoute();

	const appStore = useAppStoreWithOut();
	const globalStore = useGlobalStoreWithOut();

	const isCollapse = computed(() => !appStore.sidebar.opened);

	const sidebarRouters = computed({
		get() {
			return props.modelValue;
		},
		set(val) {
			emits('update:modelValue', val);
		},
	});

	const isShowSidebarRouters = computed(() => {
		return (
			sidebarRouters.value?.filter((itm) => {
				return !itm.hidden && !itm.meta?.hideInMenu;
			}) || []
		);
	});

	const activeMenu = computed(() => {
		const subSystemAlias = getSubSystemAliasFromLocationPathname(location.pathname);
		let fullPath = route2.fullPath;
		console.log('location.pathname', location.pathname);
		console.log('subSystemAlias', subSystemAlias);
		console.log('fullPath', fullPath);

		if (subSystemAlias) {
			const subSystem = globalStore.getSubSystemByAlias(subSystemAlias);
			if (subSystem) {
				if (fullPath.indexOf(`/${subSystemAlias}/inner/`) !== 0) {
					if (fullPath.indexOf(subSystemAlias) === 1) {
						fullPath = fullPath.replace(`/${subSystemAlias}`, '');
					}
				}
			}
		}
		// 地质61环境要加系统别名
		if (menuStore.isAddAlias) {
			return fullPath;
		}
		// 不是61环境
		const path = resolvePath(fullPath);
		return path.split('?')[0];
	});
	function resolvePath(routePath: string) {
		if (isExternal(routePath)) {
			return routePath;
		}
		const path = getNormalPath(`${routePath}`);
		// console.log('path1', path);
		return path;
	}
	const toggleSideBar = () => {
		appStore.toggleSideBar();
	};
</script>

<style scoped lang="scss">
	.sidebar-container {
		height: 100%;
		background-color: #000;
		background-image: var(--theme-pcp-sidebar-background-image);
		background-repeat: no-repeat;
		background-position: bottom;

		.sidebar-menu {
			height: calc(100% - var(--base-menu-footer-height));
			padding-top: 12px;
		}

		.menu-footer {
			height: var(--base-menu-footer-height);
			width: 100%;

			.el-divider--horizontal {
				border-top: 1px rgb(255 255 255 / 15%) solid;
				margin: 0;
			}

			.hamburger-container {
				height: 100%;
				display: flex;
				justify-content: flex-end;
				align-items: center;
				color: var(--hamburger-icon-color);
				font-size: 20px;
				cursor: pointer;
				background: unset !important;

				&:hover {
					background: var(--theme-el-menu-hover-bg-color);
					color: var(--hamburger-icon-hover-color);
				}
			}
		}
	}

	.sidebar-container.isCollapse {
		.hamburger-container {
			justify-content: center;
		}
	}
</style>

<style lang="scss">
	.sidebar-container {
		.el-menu:not(.el-menu--collapse) {
			width: var(--theme-base-sidebar-width);

			.el-sub-menu__title,
			.el-menu-item {
				margin: 0 6px;
				// padding-left: calc(var(--el-menu-level) * 10px) !important;
			}
		}

		.el-menu {
			--el-menu-active-color: var(--theme-el-menu-active-color);
			--el-menu-text-color: var(--theme-el-menu-text-color);
			--el-menu-hover-text-color: var(--theme-el-menu-hover-text-color);
			--el-menu-bg-color: var(--theme-el-menu-bg-color);
			--el-menu-hover-bg-color: var(--theme-el-menu-hover-bg-color);
			--el-menu-item-height: var(--theme-el-menu-item-height);
			--el-menu-sub-item-height: var(--theme-el-menu-sub-item-height);
			--el-menu-item-font-size: var(--theme-el-menu-item-font-size);
			--el-menu-item-hover-fill: var(--theme-el-menu-item-hover-fill);
			--el-menu-border-color: var(--theme-el-menu-border-color);
			--el-menu-base-level-padding: var(--theme-el-menu-base-level-padding);
			--el-menu-level: 0 !important;

			border: none;
			background-color: unset;

			.menu-title {
				overflow: hidden !important;
				text-overflow: ellipsis !important;
				white-space: nowrap !important;
			}

			.el-menu--inline {
				width: 100%;
			}

			.el-sub-menu__title,
			.el-menu-item {
				&:hover {
					background: var(--el-menu-hover-bg-color);
					color: var(--theme-el-menu-active-color);

					.menu-icon {
						opacity: 1;
					}
				}

				.menu-icon {
					font-size: 16px;
					margin-right: 10px !important;
					opacity: 0.5;
				}
			}

			.el-menu-item.is-active {
				background: var(--el-menu-hover-bg-color);
				color: var(--theme-el-menu-active-color);

				.menu-icon {
					opacity: 1;
				}
			}
		}
	}
</style>
